import React, { useState, useEffect, useCallback } from 'react'
import moment, { Moment } from 'moment'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import TextField from '@mui/material/TextField'
import Popover from '@mui/material/Popover'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import styled from 'styled-components'

import { color } from 'styles/colors'
import { shortDate } from 'helpers/formatters'
import { usePremiumFeaturesContext } from 'hooks/PremiumFeaturesContext'

import Day from './Day'

type Props = {
  startDate: string
  endDate: string
  onDateRangeChangeCallback: (start: string, end: string) => void
}

const AlertHistoryDateRangePicker = ({ startDate, endDate, onDateRangeChangeCallback }: Props) => {
  const { hasAccess } = usePremiumFeaturesContext()
  const hasAccessToExtendedHistory = hasAccess('extended_rthg')

  const [selectedStart, setSelectedStart] = useState<Moment | null>(moment(startDate))
  const [selectedEnd, setSelectedEnd] = useState<Moment | null>(moment(endDate))

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [isSelectingStart, setIsSelectingStart] = useState(true)
  const [hoveredDate, setHoveredDate] = useState<Moment | null>(null)

  useEffect(() => {
    setSelectedStart(moment(startDate))
    setSelectedEnd(moment(endDate))
  }, [startDate, endDate])

  const getMaxLookbackDate = useCallback(() => {
    const now = moment()
    return hasAccessToExtendedHistory ? now.clone().subtract(13, 'months').startOf('month') : now.clone().subtract(29, 'days').startOf('day')
  }, [hasAccessToExtendedHistory])

  const shouldDisableDate = useCallback((date: Moment) => {
    const now = moment()
    if (date.isAfter(now, 'day')) return true

    if (isSelectingStart) {
      const maxLookback = getMaxLookbackDate()
      if (date.isBefore(maxLookback, 'day')) return true
      return false
    }

    if (!selectedStart) return false

    const startDate = selectedStart
    const daysDiff = date.diff(startDate, 'days')

    return daysDiff >= 30 || daysDiff < 0
  }, [isSelectingStart, selectedStart, getMaxLookbackDate])

  const shouldDisableMonth = useCallback((month: Moment) => {
    const now = moment()

    if (month.isAfter(now, 'month')) return true

    const maxLookback = getMaxLookbackDate()

    if (month.isBefore(maxLookback, 'month')) return true

    return false
  }, [getMaxLookbackDate])

  const maxDate = useCallback(() => moment().endOf('month'), [])
  const minDate = useCallback(() => getMaxLookbackDate(), [getMaxLookbackDate])

  const openCalendar = useCallback((event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const closeCalendar = useCallback(() => {
    setAnchorEl(null)
    setHoveredDate(null)
  }, [])

  const setDateSelection = useCallback((date: Moment) => {
    const selectedDate = date.clone().startOf('day')

    if (isSelectingStart) {
      setSelectedStart(selectedDate)
      setSelectedEnd(null)
      setIsSelectingStart(false)
      setHoveredDate(null)
    } else if (selectedStart) {
      setSelectedEnd(selectedDate)
      setHoveredDate(null)
      onDateRangeChangeCallback(selectedStart.format('YYYY-MM-DD'), selectedDate.format('YYYY-MM-DD'))
      closeCalendar()
      setIsSelectingStart(true)
    }
  }, [isSelectingStart, selectedStart, onDateRangeChangeCallback, closeCalendar])

  const formatDateRange = useCallback(() => {
    if (selectedStart && selectedEnd) {
      if (selectedStart.isSame(selectedEnd, 'day')) {
        return shortDate(selectedStart)
      } else {
        return `${shortDate(selectedStart)} - ${shortDate(selectedEnd)}`
      }
    } else if (selectedStart) {
      return `${shortDate(selectedStart)} - Select end date`
    } else {
      return 'Select date range'
    }
  }, [selectedStart, selectedEnd])

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <S.Container>
        <TextField
          value={formatDateRange()}
          onClick={openCalendar}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              openCalendar(event)
            }
          }}
          size="small"
          slotProps={{
            input: {
              readOnly: true,
              endAdornment: (
                <IconButton
                  size="small"
                  onClick={openCalendar}
                  disableRipple
                  sx={{
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: color('actionableIconHover')
                    }
                  }}
                >
                  <S.CalendarIcon className="icon-calendar" />
                </IconButton>
              )
            }
          }}
          sx={{
            '& .MuiInputBase-root': {
              height: '36px',
              cursor: 'pointer',
              paddingRight: '0',
              width: '240px'
            },
            '& .MuiOutlinedInput-root': {
              borderRadius: '14px'
            }
          }}
        />
        <Popover
          open={!!anchorEl}
          anchorEl={anchorEl}
          onClose={closeCalendar}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
        >
          <Box sx={{ p: 2 }}>
            <div
              style={{ position: 'relative' }}
              onMouseMove={(event) => {
                if (!isSelectingStart && selectedStart && !selectedEnd) {
                  const target = event.target as HTMLElement
                  if (target.role === 'gridcell') {
                    const timestamp = target.getAttribute('data-timestamp')
                    if (timestamp) {
                      const hoveredDay = moment(parseInt(timestamp))
                      setHoveredDate(hoveredDay)
                    }
                  }
                }
              }}
              onMouseLeave={() => {
                if (!isSelectingStart && selectedStart && !selectedEnd) {
                  setHoveredDate(null)
                }
              }}
            >
              <DateCalendar
                value={null}
                shouldDisableDate={shouldDisableDate}
                shouldDisableMonth={shouldDisableMonth}
                minDate={minDate()}
                maxDate={maxDate()}
                referenceDate={selectedStart || undefined}
                slots={{
                  day: (props) => (
                    <Day
                      {...props}
                      selectedStart={selectedStart}
                      selectedEnd={selectedEnd}
                      isSelectingStart={isSelectingStart}
                      hoveredDate={hoveredDate}
                      onDateSelectCallback={setDateSelection}
                    />
                  )
                }}
              />
            </div>
          </Box>
        </Popover>
      </S.Container>
    </LocalizationProvider>
  )
}

const S = {
  Container: styled.div`
    position: relative;
    display: inline-block;
  `,
  CalendarIcon: styled.i`
    font-size: 19px;
    padding: 5px;
  `
}

export default AlertHistoryDateRangePicker
